import { z } from "zod";
import { MEDIA_CONFIG, type AllowedBucket } from "./constants";

export const mediaParamsSchema = z.object({
  w: z.coerce.number().min(1).max(MEDIA_CONFIG.MAX_WIDTH).optional(),
  h: z.coerce.number().min(1).max(MEDIA_CONFIG.MAX_HEIGHT).optional(),
  q: z.coerce
    .number()
    .min(MEDIA_CONFIG.MIN_QUALITY)
    .max(MEDIA_CONFIG.MAX_QUALITY)
    .default(MEDIA_CONFIG.DEFAULT_QUALITY),
  fit: z.enum(["cover", "contain", "fill"]).optional(),
  format: z.enum(["jpg", "jpeg", "png", "webp"]).optional(),
});

export function validateBucket(bucket: string): bucket is AllowedBucket {
  return MEDIA_CONFIG.ALLOWED_BUCKETS.includes(bucket as AllowedBucket);
}

export function sanitizePath(path: string[]): string {
  // Remove any path traversal attempts
  const sanitized = path
    .filter((segment) => segment && !segment.includes(".."))
    // Allow word chars, hyphen, dot, and colon (e.g. timestamps like 2025-08-23T...)
    .map((segment) => segment.replace(/[^\w\-\.:]/g, ""))
    .join("/");

  // Ensure no leading/trailing slashes
  return sanitized.replace(/^\/+|\/+$/g, "");
}

export function isImagePath(path: string): boolean {
  const extension = path.split(".").pop()?.toLowerCase();
  return MEDIA_CONFIG.ALLOWED_FORMATS.includes(
    extension as (typeof MEDIA_CONFIG.ALLOWED_FORMATS)[number]
  );
}

function isNotReadableError(error: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" &&
      error instanceof DOMException &&
      error.name === "NotReadableError") ||
    (typeof error === "object" &&
      error !== null &&
      "name" in error &&
      (error as any).name === "NotReadableError")
  );
}

async function readBlobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  // Prefer modern Blob.arrayBuffer(), but fall back to FileReader for Safari/WebKit edge cases.
  try {
    return await blob.arrayBuffer();
  } catch (error) {
    // Fallback: FileReader tends to be more reliable on some Safari builds.
    return await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error ?? error);
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      try {
        reader.readAsArrayBuffer(blob);
      } catch (err) {
        reject(err);
      }
    });
  }
}

/**
 * Magic byte signatures for supported file types
 * Used to verify actual file type regardless of extension or MIME type
 */
const MAGIC_BYTES = {
  jpeg: [
    [0xff, 0xd8, 0xff], // JPEG Start of Image (SOI) marker
  ],
  png: [
    [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], // PNG signature
  ],
} as const;

/**
 * Validates file type by checking magic bytes (file signature)
 * This prevents malicious files from being uploaded by disguising their extension
 *
 * @param file - The file to validate
 * @returns Promise resolving to the detected file type or null if invalid
 *
 * @example
 * ```typescript
 * const fileType = await validateFileMagicBytes(file)
 * if (!fileType) {
 *   throw new Error('Invalid file type')
 * }
 * ```
 */
export async function validateFileMagicBytes(
  file: File
): Promise<"jpeg" | "png" | null> {
  // Read first 8 bytes (enough for PNG signature, longest we check)
  const buffer = await readBlobArrayBuffer(file.slice(0, 8));
  const bytes = new Uint8Array(buffer);

  // Check JPEG signature
  for (const signature of MAGIC_BYTES.jpeg) {
    if (matchesSignature(bytes, signature)) {
      return "jpeg";
    }
  }

  // Check PNG signature
  for (const signature of MAGIC_BYTES.png) {
    if (matchesSignature(bytes, signature)) {
      return "png";
    }
  }

  return null;
}

/**
 * Checks if byte array matches a specific signature
 */
function matchesSignature(
  bytes: Uint8Array,
  signature: readonly number[]
): boolean {
  if (bytes.length < signature.length) {
    return false;
  }

  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Validates file type using both MIME type and magic bytes
 * Provides defense-in-depth against malicious file uploads
 *
 * @param file - The file to validate
 * @returns Promise resolving to validation result
 *
 * @example
 * ```typescript
 * const result = await validateFileType(file)
 * if (!result.valid) {
 *   alert(result.error)
 * }
 * ```
 */
export async function validateFileType(file: File): Promise<{
  valid: boolean;
  error?: string;
  detectedType?: "jpeg" | "png";
  warning?: string;
}> {
  // Check MIME type first (fast client-side check)
  const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png"];
  if (!allowedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      error:
        "Only JPEG and PNG image files are supported. Please select files with .jpg, .jpeg, or .png extensions.",
    };
  }

  // Verify with magic bytes (defense against spoofed MIME types)
  let detectedType: "jpeg" | "png" | null = null;
  try {
    detectedType = await validateFileMagicBytes(file);
  } catch (error) {
    // Safari/WebKit can fail to read local file bytes (NotReadableError / WebKitBlobResource).
    // In that case, we fall back to MIME validation (server still enforces allowed types).
    if (isNotReadableError(error)) {
      return {
        valid: true,
        warning:
          "Your browser could not verify the file contents (Safari read error). Continuing based on file type.",
      };
    }

    console.error("Error validating file magic bytes:", error);
    return {
      valid: false,
      error:
        "Unable to read the file for validation. Please try again, re-select the file, or move it to local storage.",
    };
  }

  if (!detectedType) {
    return {
      valid: false,
      error:
        "File does not appear to be a valid JPEG or PNG image. The file may be corrupted or have an incorrect extension.",
    };
  }

  // Check if detected type matches declared MIME type
  const declaredType =
    file.type === "image/jpeg" || file.type === "image/jpg" ? "jpeg" : "png";
  if (detectedType !== declaredType) {
    return {
      valid: false,
      error: `File type mismatch: file claims to be ${file.type} but appears to be ${detectedType}. Please verify the file is not corrupted.`,
    };
  }

  return {
    valid: true,
    detectedType,
  };
}
